import { useMemo, useState } from 'react';
import { Chip } from '@jotpsych/ui';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { formatIsoDate } from '../vm/formatters';
import type { DiagnosisVM } from '../vm/types';

import { type ActiveFilter, ClinicalCardActions } from './ClinicalCardActions';
import { ClinicalCardFrame } from './ClinicalCardFrame';
import { RowActionsMenu } from './RowActionsMenu';
import { SuggestedSection } from './SuggestedSection';

const COLUMNS = ['Diagnosis', 'Code', 'Date added', 'Resolved', 'Status', ''] as const;

const headCellSx = {
  py: 1,
  fontWeight: 600,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.6,
  color: 'text.secondary',
  bgcolor: 'backgroundColors.secondary',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const EM_DASH = '—';

interface DiagnosisRowCallbacks {
  /** Opens the edit flow for one diagnosis. */
  onEdit?: (diagnosisId: string) => void;
  /** Soft-resolves an active diagnosis (stamps the resolved date). */
  onResolve?: (diagnosisId: string) => void;
  /** Reactivates a resolved diagnosis. */
  onReactivate?: (diagnosisId: string) => void;
  /** Promotes an AI-suggested diagnosis (PATCH status='accepted'). */
  onAcceptSuggestion?: (diagnosisId: string) => void;
  /** Dismisses an AI-suggested diagnosis (PATCH status='rejected'). */
  onDismissSuggestion?: (diagnosisId: string) => void;
}

interface DiagnosesCardProps extends DiagnosisRowCallbacks {
  diagnoses: DiagnosisVM[];
  initialFilter?: ActiveFilter;
  /** Opens the add-diagnosis flow ("+ Add"). */
  onAdd?: () => void;
  /** Notification for the host's analytics — fired after the filter changes. */
  onFilterChanged?: (filter: ActiveFilter) => void;
  /** When true, drops the Suggested section entirely (host reads feature_show_chart_suggestions; default off → hidden). */
  hideSuggestions?: boolean;
  /** Notification for the host's analytics — Suggested section caret toggled. */
  onSuggestionsSectionToggled?: (expanded: boolean) => void;
  /** 'embedded' hands the chrome and scroll to a dialog host. */
  variant?: 'card' | 'embedded';
  /** Embedded only: renders the header's close button (the dialog's X). */
  onClose?: () => void;
  /** Drops the card's own title for hosts that already render one (the V3 section shell). */
  hideTitle?: boolean;
  /** Optional host-specific cap for the primary diagnosis table. */
  tableMaxHeight?: number | string;
}

interface DiagnosisTableProps extends DiagnosisRowCallbacks {
  rows: DiagnosisVM[];
  stickyHeader?: boolean;
}

const DiagnosisTable: React.FC<DiagnosisTableProps> = ({
  rows,
  stickyHeader = false,
  onEdit,
  onResolve,
  onReactivate,
  onAcceptSuggestion,
  onDismissSuggestion,
}) => (
  <Table size="small" stickyHeader={stickyHeader} sx={{ minWidth: 640 }}>
    <TableHead>
      <TableRow>
        {COLUMNS.map((column, index) => (
          <TableCell
            key={column || 'actions'}
            align={index === COLUMNS.length - 1 ? 'right' : 'left'}
            sx={{ ...headCellSx, width: index === 0 ? '40%' : undefined }}
          >
            {column}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map(dx => (
        <TableRow key={dx.id}>
          <TableCell sx={{ py: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dx.name}
            </Typography>
          </TableCell>
          <TableCell sx={{ py: 1.5 }}>
            {dx.code ? (
              <Chip variant="soft" size="xSmall" color="default" label={dx.code} />
            ) : (
              <Typography variant="body2" color="text.disabled">
                {EM_DASH}
              </Typography>
            )}
          </TableCell>
          <TableCell sx={{ py: 1.5 }}>
            <Typography variant="body2">{formatIsoDate(dx.dateAdded)}</Typography>
          </TableCell>
          <TableCell sx={{ py: 1.5 }}>
            <Typography variant="body2" color={dx.endDate ? 'text.primary' : 'text.disabled'}>
              {dx.endDate ? formatIsoDate(dx.endDate) : EM_DASH}
            </Typography>
          </TableCell>
          <TableCell sx={{ py: 1.5 }}>
            {dx.isSuggested ? (
              <Chip variant="soft" size="xSmall" color="info" label="Suggested" />
            ) : (
              <Chip
                variant="soft"
                size="xSmall"
                color={dx.isActive ? 'success' : 'default'}
                label={dx.isActive ? 'Active' : 'Resolved'}
              />
            )}
          </TableCell>
          <TableCell align="right" sx={{ py: 1.5 }}>
            <RowActionsMenu
              ariaLabel={`Actions for ${dx.name}`}
              onSelect={key => {
                if (key === 'edit') onEdit?.(dx.id);
                if (key === 'resolve') onResolve?.(dx.id);
                if (key === 'reactivate') onReactivate?.(dx.id);
                if (key === 'accept') onAcceptSuggestion?.(dx.id);
                if (key === 'dismiss') onDismissSuggestion?.(dx.id);
              }}
              actions={
                dx.isSuggested
                  ? [
                      {
                        key: 'accept',
                        label: 'Accept suggestion',
                        icon: <CheckCircleOutlinedIcon fontSize="small" />,
                      },
                      {
                        key: 'dismiss',
                        label: 'Dismiss',
                        icon: <BlockOutlinedIcon fontSize="small" />,
                        destructive: true,
                      },
                    ]
                  : [
                      {
                        key: 'edit',
                        label: 'Edit',
                        icon: <EditOutlinedIcon fontSize="small" />,
                      },
                      dx.isActive
                        ? {
                            key: 'resolve',
                            label: 'Mark resolved',
                            icon: <CheckCircleOutlinedIcon fontSize="small" />,
                          }
                        : {
                            key: 'reactivate',
                            label: 'Reactivate',
                            icon: <CheckCircleOutlinedIcon fontSize="small" />,
                          },
                    ]
              }
            />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/**
 * Diagnoses card on the shared clinical-card pattern (same frame, filter, and
 * kebab actions as the Medications card). Replaces the legacy DataGrid's
 * Active checkbox with a status chip; "Mark resolved" is a SOFT action.
 * AI-suggested diagnoses fold into a collapsed Suggested section (GDN-2472)
 * so the confirmed problem list leads the card.
 * Presentational — renders DiagnosisVM[] and delegates every write to the host.
 */
export const DiagnosesCard: React.FC<DiagnosesCardProps> = ({
  diagnoses,
  initialFilter = 'all',
  onAdd,
  onEdit,
  onResolve,
  onReactivate,
  onAcceptSuggestion,
  onDismissSuggestion,
  onFilterChanged,
  hideSuggestions = false,
  onSuggestionsSectionToggled,
  variant = 'card',
  onClose,
  hideTitle,
  tableMaxHeight,
}) => {
  const [filter, setFilter] = useState<ActiveFilter>(initialFilter);

  const handleFilterChange = (value: ActiveFilter) => {
    setFilter(value);
    onFilterChanged?.(value);
  };

  const suggested = useMemo(
    () => (hideSuggestions ? [] : diagnoses.filter(dx => dx.isSuggested)),
    [diagnoses, hideSuggestions],
  );

  const rows = useMemo(() => {
    const accepted = diagnoses.filter(dx => !dx.isSuggested);
    if (filter === 'active') return accepted.filter(dx => dx.isActive);
    if (filter === 'inactive') return accepted.filter(dx => !dx.isActive);
    return [...accepted.filter(dx => dx.isActive), ...accepted.filter(dx => !dx.isActive)];
  }, [diagnoses, filter]);

  const rowCallbacks = {
    onEdit,
    onResolve,
    onReactivate,
    onAcceptSuggestion,
    onDismissSuggestion,
  };

  return (
    <ClinicalCardFrame
      title="Diagnoses"
      hideTitle={hideTitle}
      variant={variant}
      onClose={onClose}
      actions={
        <ClinicalCardActions
          filter={filter}
          onFilterChange={handleFilterChange}
          filterLabel="Filter diagnoses"
          inactiveLabel="Resolved"
          onAdd={onAdd}
        />
      }
    >
      {rows.length === 0 ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {filter === 'inactive' ? 'No resolved diagnoses.' : 'No active diagnoses.'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            overflowX: 'auto',
            maxHeight: tableMaxHeight,
            overflowY: tableMaxHeight ? 'auto' : undefined,
            ...(variant === 'embedded' && {
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
            }),
          }}
        >
          <DiagnosisTable
            rows={rows}
            stickyHeader={Boolean(tableMaxHeight) || variant === 'embedded'}
            {...rowCallbacks}
          />
        </Box>
      )}
      <SuggestedSection
        count={suggested.length}
        itemNoun="diagnoses"
        onSectionToggled={onSuggestionsSectionToggled}
        contentMaxHeight={variant === 'embedded' ? '38vh' : undefined}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <DiagnosisTable rows={suggested} {...rowCallbacks} />
        </Box>
      </SuggestedSection>
    </ClinicalCardFrame>
  );
};
